import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dumbbell, User, Lock, Loader2, CheckCircle, XCircle, Mail,
  Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown
} from 'lucide-react';
import client from '../api/client';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useDesign } from '../contexts/DesignContext';

const LOGO_ICONS = { Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown };

export default function CompleteSignupPage() {
  const { t } = useTranslation();
  const { design } = useDesign();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const pc = design.primary_color || '#FF5C00';
  const LogoIcon = LOGO_ICONS[design.logo_icon] || Dumbbell;
  const appLeft = design.app_name_left || 'trainedby';
  const appRight = design.app_name_right || 'RS';
  const hf = design.heading_font || 'Barlow Condensed';
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('completeSignup.noToken'));
      setValidating(false);
      setLoading(false);
      return;
    }
    
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await client.get(`/invites/validate/${token}`);
      setInviteData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || t('completeSignup.invalidLink'));
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('completeSignup.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 8) {
      toast.error(t('completeSignup.passwordTooShort'));
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await client.post('/invites/complete', {
        token,
        email,
        name,
        password
      });
      
      // Auto-login - just set the token and user directly
      const { token: authToken, user: userData } = response.data;
      localStorage.setItem('token', authToken);
      toast.success(t('completeSignup.accountCreated'));

      // Reload the page to trigger AuthContext to pick up the new token
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || t('completeSignup.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">{t('completeSignup.validating')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('completeSignup.invalidInviteTitle')}</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              {t('completeSignup.goToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 rounded-xl" style={{ backgroundColor: pc }}>
              <LogoIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {appLeft}<span style={{ color: pc }}>{appRight}</span>
            </span>
          </div>
          <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('completeSignup.completeRegistration')} <span className="text-primary">{t('completeSignup.registration')}</span>
          </CardTitle>
          <CardDescription>
            <span className="font-medium text-foreground">{inviteData?.inviter_name}</span> {t('completeSignup.invitedToJoin')}
            {inviteData?.invite_type === 'client_assignment' && ` ${t('completeSignup.asTheirClient')}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-6 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-sm">{t('completeSignup.invitedBy')} <strong>{inviteData?.inviter_name}</strong>{inviteData?.invite_type === 'client_assignment' ? ` ${t('completeSignup.asTheirClient')}` : ''}</span>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('completeSignup.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="signup-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.fullName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  placeholder={t('completeSignup.fullNamePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="signup-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder={t('completeSignup.createPasswordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                  data-testid="signup-password"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword', 'Confirm Password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder={t('completeSignup.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="signup-confirm-password"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={submitting || !email || !name || !password || !confirmPassword}
              data-testid="complete-signup-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('completeSignup.creatingAccount')}
                </>
              ) : (
                t('completeSignup.createAccount')
              )}
            </Button>
          </form>
          
          <p className="text-center text-sm text-muted-foreground mt-4">
            {t('completeSignup.alreadyHaveAccount')}{' '}
            <button onClick={() => navigate('/login')} className="text-primary hover:underline">
              {t('completeSignup.signIn')}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
